import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail, MessageCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToId, waLink, WA_DISPLAY, EMAIL } from "../data/content";
import { useLang, useT } from "../i18n";

const ITEMS = [
  { key: "home", hash: "#home", testid: "nav-link-home" },
  { key: "about", hash: "#about", testid: "nav-link-about" },
  { key: "products", hash: "#products", testid: "nav-link-products" },
  { key: "partners", hash: "#partners", testid: "nav-link-partners" },
  { key: "quality", hash: "#quality", testid: "nav-link-quality" },
  { key: "markets", hash: "#markets", testid: "nav-link-markets" },
  { key: "contact", hash: "#contact", testid: "nav-link-contact" },
];

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-slate-700" data-testid="lang-toggle">
      {["en", "id"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 font-mono text-[10px] tracking-[0.2em] transition-[background-color,color] duration-300 ${
            lang === l ? "bg-gold text-[#0B132B]" : "text-slate-400 hover:text-gold"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    if (pathname !== "/") navigate(`/${hash}`);
    else scrollToId(hash);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || pathname !== "/" ? "border-b border-gold/10 bg-[#0B132B]/95 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <button data-testid="nav-logo" onClick={() => go("#home")} className="flex shrink-0 items-center gap-3" aria-label="CRESSCO DAYA UTAMA home">
          <span className="flex h-11 items-center rounded-sm bg-white px-2.5 py-1">
            <img src="/assets/logo.jpeg" alt="CRESSCO DAYA UTAMA logo" className="h-9 w-auto object-contain" />
          </span>
          <span className="hidden flex-col leading-none md:flex">
            <span className="font-display text-base font-semibold tracking-[0.14em] text-white">CRESSCO</span>
            <span className="font-mono text-[8px] tracking-[0.35em] text-gold">DAYA UTAMA</span>
          </span>
        </button>

        <nav className="hidden items-center gap-6 xl:flex">
          {ITEMS.map((l) => (
            <button
              key={l.key}
              data-testid={l.testid}
              onClick={() => go(l.hash)}
              className="group relative whitespace-nowrap font-mono text-[11px] tracking-[0.18em] text-slate-300 transition-colors duration-300 hover:text-gold"
            >
              {t.nav[l.key].toUpperCase()}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 xl:flex">
          <LangToggle />
          <a
            data-testid="nav-whatsapp-link"
            href={waLink(t.wa.generic)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] tracking-wider text-slate-300 transition-colors duration-300 hover:text-gold"
          >
            <MessageCircle size={13} className="text-gold" /> {WA_DISPLAY}
          </a>
          <a
            data-testid="nav-email-link"
            href={`mailto:${EMAIL}`}
            className="flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] tracking-wider text-slate-300 transition-colors duration-300 hover:text-gold"
          >
            <Mail size={13} className="text-gold" /> {EMAIL}
          </a>
          <button
            data-testid="nav-cta-request-quote"
            onClick={() => go("#quote")}
            className="whitespace-nowrap border border-gold/60 px-5 py-2.5 font-mono text-[11px] tracking-[0.18em] text-gold transition-[background-color,color,box-shadow] duration-300 hover:bg-gold hover:text-[#0B132B] hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]"
          >
            {t.nav.quote.toUpperCase()}
          </button>
        </div>

        <div className="flex items-center gap-3 xl:hidden">
          <LangToggle />
          <a
            data-testid="nav-whatsapp-mobile"
            href={waLink(t.wa.generic)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="text-gold"
          >
            <MessageCircle size={22} />
          </a>
          <button data-testid="nav-mobile-toggle" className="text-slate-200" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-b border-gold/10 bg-[#0B132B]/95 backdrop-blur-md xl:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {ITEMS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.hash)}
                  className="py-3 text-left font-mono text-xs tracking-[0.22em] text-slate-200 transition-colors duration-300 hover:text-gold"
                >
                  {t.nav[l.key].toUpperCase()}
                </button>
              ))}
              <a href={`mailto:${EMAIL}`} className="py-3 font-mono text-xs tracking-[0.22em] text-slate-400">{EMAIL}</a>
              <button
                data-testid="nav-cta-request-quote-mobile"
                onClick={() => go("#quote")}
                className="mt-2 border border-gold/60 py-3 text-center font-mono text-xs tracking-[0.22em] text-gold"
              >
                {t.nav.quote.toUpperCase()}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
